/**
 * User Slice
 * Manages user profile, preferences, and settings
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Mock API calls
const mockFetchUserProfile = async (userId) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        id: userId,
        email: 'mamadou.diallo@ecomotech.com',
        name: 'Mamadou Diallo',
        role: 'admin',
        avatar: null,
        department: 'IT',
        position: 'System Administrator',
        phone: '+224 620 111 222',
        preferences: {
          theme: 'light',
          language: 'fr',
          notifications: {
            email: true,
            push: true,
            sms: false,
          },
          dashboard: {
            layout: 'grid',
            widgets: ['metrics', 'charts', 'alerts'],
          },
        },
      });
    }, 500);
  });
};

const mockUpdateUserProfile = async (userId, updates) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({ ...updates, updatedAt: new Date().toISOString() });
    }, 500);
  });
};

// Async thunks
export const fetchUserProfile = createAsyncThunk(
  'user/fetchProfile',
  async (userId, { rejectWithValue }) => {
    try {
      const profile = await mockFetchUserProfile(userId);
      return profile;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'user/updateProfile',
  async ({ userId, updates }, { rejectWithValue }) => {
    try {
      const updatedProfile = await mockUpdateUserProfile(userId, updates);
      return updatedProfile;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateUserPreferences = createAsyncThunk(
  'user/updatePreferences',
  async (preferences, { rejectWithValue }) => {
    try {
      // In a real app, this would call an API
      return preferences;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Initial state
const initialState = {
  profile: null,
  preferences: {
    theme: 'light',
    language: 'en',
    notifications: {
      email: true,
      push: true,
      sms: false,
    },
    dashboard: {
      layout: 'grid',
      widgets: ['metrics', 'charts', 'alerts'],
    },
  },
  isLoading: false,
  error: null,
};

// Slice
const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setTheme: (state, action) => {
      state.preferences.theme = action.payload;
    },
    setLanguage: (state, action) => {
      state.preferences.language = action.payload;
    },
    toggleNotification: (state, action) => {
      const { type } = action.payload;
      state.preferences.notifications[type] = !state.preferences.notifications[type];
    },
    updateDashboardLayout: (state, action) => {
      state.preferences.dashboard.layout = action.payload;
    },
    updateDashboardWidgets: (state, action) => {
      state.preferences.dashboard.widgets = action.payload;
    },
    clearUserData: (state) => {
      state.profile = null;
      state.preferences = initialState.preferences;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch User Profile
      .addCase(fetchUserProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profile = action.payload;
        state.preferences = action.payload.preferences || state.preferences;
        state.error = null;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch user profile';
      })
      // Update User Profile
      .addCase(updateUserProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profile = { ...state.profile, ...action.payload };
        state.error = null;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to update user profile';
      })
      // Update User Preferences
      .addCase(updateUserPreferences.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateUserPreferences.fulfilled, (state, action) => {
        state.isLoading = false;
        state.preferences = { ...state.preferences, ...action.payload };
      })
      .addCase(updateUserPreferences.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to update preferences';
      });
  },
});

// Actions
export const {
  setTheme,
  setLanguage,
  toggleNotification,
  updateDashboardLayout,
  updateDashboardWidgets,
  clearUserData,
} = userSlice.actions;

// Selectors
export const selectUserProfile = (state) => state.user.profile;
export const selectUserPreferences = (state) => state.user.preferences;
export const selectUserTheme = (state) => state.user.preferences.theme;
export const selectUserLanguage = (state) => state.user.preferences.language;
export const selectUserLoading = (state) => state.user.isLoading;
export const selectUserError = (state) => state.user.error;

export default userSlice.reducer;
